import { useConfig } from "@jti/app-context/useConfig";
import type { AlertBannerAction } from "@jti/core-components/components/AlertBanner";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";
import { usePdpLink } from "@jti/magento";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { TradeInDisabledCause } from "@product-details/containers/StandardProductDetailsContainer/components/types";

type TradeInAlertBannerProps = {
  cause: TradeInDisabledCause;
  nonTradeInSku: string;
  onRegisterDeviceClick?: () => void;
};

export const TradeInAlertBanner = ({ cause, nonTradeInSku, onRegisterDeviceClick }: TradeInAlertBannerProps) => {
  const { __ } = useTranslations();
  const config = useConfig();

  const pdpLink = usePdpLink(nonTradeInSku, { shopUrl: config.SHOP_URL, pdpUrl: config.PDP_URL ?? "" });

  const relatedProductPageHref = pdpLink || config.SHOP_URL;

  const viewRelatedProductText = __("productdetails.tradein.error.view_related_device");
  const buttonFirstText = onRegisterDeviceClick ? __("productdetails.tradein.error.register") : undefined;

  const sharedProps = {
    title: __("productdetails.tradein.error.title"),
    tone: "warning",
  } as const;

  const onNotEligibleViewRelatedProductClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: viewRelatedProductText,
      cta_location: "trade-in_alert_banner",
    });

    window.location.href = relatedProductPageHref;
  };

  const onDeviceNotRegisteredViewRelatedProductClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: viewRelatedProductText,
      cta_location: "trade-in_alert_banner",
    });

    window.location.href = relatedProductPageHref;
  };

  const onDeviceNotRegisteredRegisterClick = async () => {
    if (onRegisterDeviceClick) {
      onRegisterDeviceClick();
    }

    if (buttonFirstText) {
      await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
        cta_name: buttonFirstText,
        cta_location: "trade-in_alert_banner",
      });
    }
  };

  if (cause === "not-eligible") {
    return (
      <AlertBanner
        description={__("productdetails.tradein.error.not_eligible.text")}
        actions={[
          {
            label: viewRelatedProductText,
            onClick: onNotEligibleViewRelatedProductClick,
          },
        ]}
        {...sharedProps}
      />
    );
  }

  const viewRelatedProductAction: AlertBannerAction = {
    label: viewRelatedProductText,
    onClick: onDeviceNotRegisteredViewRelatedProductClick,
  };

  const deviceNotRegisteredActions: [AlertBannerAction] | [AlertBannerAction, AlertBannerAction] = buttonFirstText
    ? [{ label: buttonFirstText, onClick: onDeviceNotRegisteredRegisterClick }, viewRelatedProductAction]
    : [viewRelatedProductAction];

  return (
    <AlertBanner
      description={__("productdetails.tradein.error.device_not_registered.text")}
      actions={deviceNotRegisteredActions}
      {...sharedProps}
    />
  );
};
